import { STATUS } from './constants/statuses'
import { LEVEL, LEVEL_ORDER, REQUIREMENT } from './constants/levels'
import { isCompleteStatus } from './displayUtils'

// Requirements that should be auto-locked based on prior requirement completion
const AUTO_LOCKED_REQUIREMENTS = {
  [LEVEL.QUALIFYING]: [],
  [LEVEL.LEVEL_1]: [REQUIREMENT.PURCHASE],
  [LEVEL.LEVEL_2]: [REQUIREMENT.PURCHASE],
  [LEVEL.LEVEL_3]: [REQUIREMENT.FINAL_EXAM, REQUIREMENT.ENROLLMENT],
}

// Check if a requirement should be auto-locked (not a selectable option)
export const isAutoLockedRequirement = (levelKey, requirementId) => {
  return AUTO_LOCKED_REQUIREMENTS[levelKey]?.includes(requirementId) || false
}

// Verifies that a requirement is complete in terms of enabling progression to the next requirement/level
// PAT Qualified status allows progression through levels but is not considered complete until Passed status is achieved
const isRequirementSatisfiedForProgression = (requirement) => {
  if (requirement.id === REQUIREMENT.PLAYING_ABILITY_TEST) {
    return requirement.status === STATUS.COMPLETE || requirement.status === STATUS.QUALIFIED
  }
  return requirement.status === STATUS.COMPLETE
}

const hasAllRequirementsSatisfied = (requirements) => {
  if (!requirements || requirements.length === 0) return false
  return requirements.every(req => isRequirementSatisfiedForProgression(req))
}

// Verifies that all requirements in a level are complete leading up to a locked requirement (eg., Purchase Next Level)
const hasCompletedLevelRequirements = (level, excludeRequirementId) => {
  return level.requirements.every((req) => {
    if (req.id === excludeRequirementId) return true
    return isRequirementSatisfiedForProgression(req)
  })
}

const isEnrollmentEligible = (levelState, level) => {
  // Check if Portfolio is complete
  const portfolioRequirement = level.requirements.find(
    (req) => req.id === REQUIREMENT.PORTFOLIO,
  )
  if (!portfolioRequirement || !isCompleteStatus(portfolioRequirement.status)) {
    return false
  }

  // Check if PAT is complete or qualified
  const patRequirement = levelState[LEVEL.QUALIFYING].requirements.find(
    (req) => req.id === REQUIREMENT.PLAYING_ABILITY_TEST,
  )
  if (!patRequirement || !isCompleteStatus(patRequirement.status)) {
    return false
  }

  return true
}

// Determine the current 'in progress' level based on requirement completion or program history
export const getCurrentLevel = (levelState, programHistoryData = null) => {
  // Currently qualifying completion is determined by prospect data, not levelState
  const associateLevels = LEVEL_ORDER.slice(1)

  // If program history data is provided, use it to determine the current level
  if (
    programHistoryData?.currentLevel &&
    programHistoryData?.LEVEL_KEY_TO_CODE
  ) {
    const { currentLevel: currentLevelCode, LEVEL_KEY_TO_CODE } =
      programHistoryData

    // Find which levelKey corresponds to the current level code
    for (const levelKey of associateLevels) {
      if (LEVEL_KEY_TO_CODE[levelKey] === currentLevelCode) {
        return levelKey
      }
    }
  }

  // Fall back to original logic: find first level with incomplete requirements
  for (let i = 0; i < associateLevels.length; i++) {
    const levelKey = associateLevels[i]
    const level = levelState[levelKey]

    // Check if this level has any incomplete requirements
    const hasIncompleteRequirements = level.requirements.some((req) => {
      return !isRequirementSatisfiedForProgression(req)
    })

    if (hasIncompleteRequirements) {
      return levelKey
    }
  }

  // If all levels are complete, return the last level
  return LEVEL.LEVEL_3
}

// Check if a requirement should be locked (disabled in the UI)
export const shouldRequirementBeLocked = (
  levelKey,
  requirementId,
  levelState,
  programHistoryData = null,
) => {
  const currentLevel = getCurrentLevel(levelState, programHistoryData)
  const currentLevelIndex = LEVEL_ORDER.indexOf(currentLevel)
  const requirementLevelIndex = LEVEL_ORDER.indexOf(levelKey)

  // PAT is never locked
  if (levelKey === LEVEL.QUALIFYING && requirementId === REQUIREMENT.PLAYING_ABILITY_TEST) {
    return false
  }

  // Application is never locked
  if (levelKey === LEVEL.QUALIFYING && requirementId === REQUIREMENT.APPLICATION) {
    return false
  }

  // Lock requirements in levels beyond the current level
  // Only the current level (where there are incomplete requirements) should be accessible
  if (requirementLevelIndex > currentLevelIndex) {
    return true
  }

  // For auto-locked requirements, check if prior requirements are complete
  if (isAutoLockedRequirement(levelKey, requirementId)) {
    const level = levelState[levelKey]
    const requirementIndex = level.requirements.findIndex(
      (req) => req.id === requirementId,
    )

    // Enrollment: Only requires Portfolio and PAT to be complete
    if (levelKey === LEVEL.LEVEL_3 && requirementId === REQUIREMENT.ENROLLMENT) {
      return !isEnrollmentEligible(levelState, level)
    }

    // Purchase: Requires all other requirements in the level to be complete
    if (requirementId === REQUIREMENT.PURCHASE) {
      return !hasCompletedLevelRequirements(level, REQUIREMENT.PURCHASE)
    }

    // For other auto-locked requirements (like final_exam): Check if all prior requirements in this level are complete
    for (let i = 0; i < requirementIndex; i++) {
      const priorReq = level.requirements[i]

      if (!isRequirementSatisfiedForProgression(priorReq)) {
        return true
      }
    }
  }

  return false
}

// Calculate the appropriate status for auto-locked requirements
export const calculateAutoLockedStatus = (
  levelKey,
  requirementId,
  levelState,
) => {
  if (!isAutoLockedRequirement(levelKey, requirementId)) {
    return null
  }

  const level = levelState[levelKey]
  const requirementIndex = level.requirements.findIndex(
    (req) => req.id === requirementId,
  )

  const currentStatus = level.requirements[requirementIndex]?.status

  // Enrollment: Only requires Portfolio and PAT to be complete
  if (levelKey === LEVEL.LEVEL_3 && requirementId === REQUIREMENT.ENROLLMENT) {
    if (!isEnrollmentEligible(levelState, level)) {
      return STATUS.LOCKED
    }
    return currentStatus === STATUS.LOCKED ? STATUS.OPEN : currentStatus
  }

  // Purchase: Requires all other requirements in the level to be complete
  if (requirementId === REQUIREMENT.PURCHASE) {
    if (!hasCompletedLevelRequirements(level, REQUIREMENT.PURCHASE)) {
      return STATUS.LOCKED
    }
    return currentStatus === STATUS.LOCKED ? STATUS.OPEN : currentStatus
  }

  // For other auto-locked requirements (like final_exam): Check if all prior requirements in this level are complete
  for (let i = 0; i < requirementIndex; i++) {
    const priorReq = level.requirements[i]

    if (!isRequirementSatisfiedForProgression(priorReq)) {
      return STATUS.LOCKED
    }
  }

  // If not locked, return 'open' as the default status (or maintain current status if not locked)
  return currentStatus === STATUS.LOCKED ? STATUS.OPEN : currentStatus
}

// Apply default statuses to all requirements based on current progress through program
export const setDefaultRequirementStatuses = (
  levelState,
  programHistoryData = null,
) => {
  const newLevelState = JSON.parse(JSON.stringify(levelState))

  LEVEL_ORDER.forEach((levelKey) => {
    const level = newLevelState[levelKey]
    level.requirements.forEach((requirement) => {
      // Calculate and apply auto-locked status for special requirements (Application, Final Exam, Enrollment)
      if (isAutoLockedRequirement(levelKey, requirement.id)) {
        const autoLockedStatus = calculateAutoLockedStatus(
          levelKey,
          requirement.id,
          newLevelState,
          programHistoryData,
        )
        if (autoLockedStatus !== null) {
          requirement.status = autoLockedStatus
        }
        return
      }

      const shouldBeLocked = shouldRequirementBeLocked(
        levelKey,
        requirement.id,
        newLevelState,
        programHistoryData,
      )

      if (shouldBeLocked) {
        requirement.status = STATUS.LOCKED
      } else if (requirement.status === STATUS.LOCKED) {
        requirement.status = STATUS.OPEN
      }
    })
  })

  return newLevelState
}

export const getCompletedLevels = (programHistory = []) => {
  return new Set(
    programHistory
      .filter((program) => program.completeCode?.toLowerCase() === STATUS.COMPLETE)
      .map((program) => program.levelCode),
  )
}

export const getCurrentLevelCode = (programHistory = []) => {
  const currentProgram = programHistory.find((program) => !program.completeCode)
  return currentProgram?.levelCode || null
}

export const getLevelKeyFromCode = (levelCode) => {
  if (!levelCode) return null
  if (levelCode.endsWith('0')) return LEVEL.QUALIFYING
  if (levelCode.endsWith('1')) return LEVEL.LEVEL_1
  if (levelCode.endsWith('2')) return LEVEL.LEVEL_2
  if (levelCode.endsWith('3')) return LEVEL.LEVEL_3
  return null
}

// Checks if the user has advanced to or past a target level. 
// Based on current level, and program history as a fallback
export const hasAdvancedToLevel = (targetLevelKey, currentLevelCode, programHistory = []) => {
  const targetPosition = LEVEL_ORDER.indexOf(targetLevelKey)
  if (targetPosition === -1) return false

  const currentLevelKey = getLevelKeyFromCode(currentLevelCode)
  const currentPosition = LEVEL_ORDER.indexOf(currentLevelKey)
  
  if (currentPosition >= targetPosition) {
    return true
  }

  return programHistory?.some(p => {
    const historyLevelKey = getLevelKeyFromCode(p.levelCode)
    return LEVEL_ORDER.indexOf(historyLevelKey) >= targetPosition
  }) || false
}

const areProspectRequirementsComplete = (prospect) => {
  if (!prospect) return false

  const backgroundCheckStatus = prospect.backgroundCheck?.status?.toLowerCase()
  const patStatus = prospect.playingAbilityTestStatus?.status?.toLowerCase()
  const qualifyingLevelStatus = prospect.qualifyingLevel?.status?.toLowerCase()

  const backgroundCheckComplete = backgroundCheckStatus === STATUS.APPROVED
  const patComplete = patStatus === STATUS.PASSED || patStatus === STATUS.COMPLETE
  const qualifyingLevelComplete = prospect.qualifyingLevel?.completedAt && 
                                   qualifyingLevelStatus !== STATUS.EXPIRED

  return backgroundCheckComplete && patComplete && qualifyingLevelComplete
}

const calculateLevelProgress = (requirements) => {
  if (!requirements || requirements.length === 0) return 0
  const satisfiedRequirementCount = requirements.filter(req => isRequirementSatisfiedForProgression(req)).length
  return Math.round((satisfiedRequirementCount / requirements.length) * 100)
}

const getLevelStatus = (requirements) => {
  if (!requirements || requirements.length === 0) return STATUS.NOT_STARTED
  const allRequirementsSatisfied = hasAllRequirementsSatisfied(requirements)
  const someInProgress = requirements.some(req => req.status === STATUS.IN_PROGRESS || req.status === STATUS.OPEN)

  if (allRequirementsSatisfied) return STATUS.COMPLETE
  if (someInProgress) return STATUS.IN_PROGRESS
  return STATUS.NOT_STARTED
}

export const buildLevelTrackerData = (levelState, activeLevel, programHistory = [], currentLevelCode) => {
  const currentLevel = getCurrentLevel(levelState, null)
  const currentLevelIndex = LEVEL_ORDER.indexOf(currentLevel)
  const mostRecentLevelKey = getLevelKeyFromCode(currentLevelCode)
  
  return LEVEL_ORDER.map((key, index) => {
    const level = levelState[key]
    const programLevel = programHistory?.find(p => getLevelKeyFromCode(p.levelCode) === key)
    
    let progress = calculateLevelProgress(level.requirements)
    let status = getLevelStatus(level.requirements)
    const isActive = key === activeLevel
    const isLocked = index > currentLevelIndex
    const isPriorLevel = key !== mostRecentLevelKey

    // If user has progressed to a later level, all prior levels are inferred to be complete
    if (isPriorLevel && programLevel) {
      progress = 100
      status = STATUS.COMPLETE
    }

    return {
      key,
      name: level.name,
      completed: status === STATUS.COMPLETE,
      active: isActive,
      locked: isLocked,
      progress,
      status
    }
  })
}

export const getLevelCompletionDate = (levelKey, programHistory = [], levelCodeMapping = {}) => {
  if (!programHistory || programHistory.length === 0) return null

  const levelCode = levelCodeMapping[levelKey]
  if (!levelCode) return null

  const levelProgram = programHistory.find(
    (program) => program.levelCode === levelCode && program.completeCode?.toLowerCase() === STATUS.COMPLETE
  )

  return levelProgram?.endDate || null
}

// Determine if a level should show the LevelCompleteBanner
// This happens when:
// 1. The user is viewing a prior completed level (not their current level)
// 2. AND the level has incomplete/missing requirement data
export const shouldInferLevelComplete = (
  levelKey,
  programHistory = [],
  currentLevelCode,
  levelState = null,
  prospect = null
) => {
  if (!programHistory || programHistory.length === 0) return false
  if (!currentLevelCode) return false

  // Find the program record for the level being checked
  const currentProgramLevel = programHistory.find(
    p => getLevelKeyFromCode(p.levelCode) === levelKey
  )
  if (!currentProgramLevel) return false

  // Only show banner if the user has advanced past this level (to the next level or beyond)
  const levelPosition = LEVEL_ORDER.indexOf(levelKey)
  const nextLevelKey = LEVEL_ORDER[levelPosition + 1]
  
  if (!nextLevelKey || !hasAdvancedToLevel(nextLevelKey, currentLevelCode, programHistory)) {
    return false
  }

  // For qualifying levels, check if prospect requirements are complete
  if (currentProgramLevel.levelCode?.endsWith('0')) {
    if (prospect) {
      return !areProspectRequirementsComplete(prospect)
    }
    // Fall back to levelState check if no prospect data
    if (!levelState || !levelState[levelKey] || !levelState[levelKey].requirements) {
      return true
    }
    const level = levelState[levelKey]
    return !hasAllRequirementsSatisfied(level.requirements)
  }

  // For all other prior levels, check if all requirements in levelState are complete
  if (!levelState) {
    return true
  }

  const level = levelState[levelKey]
  if (!level || !level.requirements) {
    return true
  }

  return !hasAllRequirementsSatisfied(level.requirements)
}
